import React, { useEffect, useState } from "react";
import { Plus, Trash2, Printer } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr } from "../lib/api";
import { printDoc } from "../lib/printDoc";
import { useLang } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const LANGS = ["pt", "tet", "en"];
const LABEL = { pt: "Português", tet: "Tetum", en: "English" };

export function ContractTemplatesCard() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [use, setUse] = useState("professional");
  const [tpl, setTpl] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => API.get("/shop/contract-templates").then((r) => { setData(r.data); setTpl(r.data.templates[use]); }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (data) setTpl(data.templates[use]); }, [use]);

  if (!data || !tpl) return null;

  const printForLawyer = () => {
    const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;");
    const langs = data.langs;
    const block = (u) => {
      const tp = data.templates[u];
      return `<section style="page-break-after:always"><h1>${langs.map((l) => esc(tp.title?.[l])).filter(Boolean).join(" / ")}</h1>
<h2>${langs.map((l) => esc(tp.subtitle?.[l])).filter(Boolean).join(" · ")}</h2>
<p class="meta">${esc(t("tpl.version"))}: ${tp.version} · ${esc(t("ct.item_use"))}: ${esc(t(`ct.use_${u}`))} · ${esc(t("tpl.lawyer_note"))}</p>
${tp.articles.map((a) => `<article><h3>Artigu ${a.no}º — ${langs.map((l) => esc(a.title?.[l])).filter(Boolean).join(" / ")}</h3>
${langs.map((l) => a.body?.[l] ? `<p><b>[${l.toUpperCase()}]</b> ${esc(a.body[l]).replace(/\{[a-z_]+\}/g, (m) => `<u>${m}</u>`).replace(/\n/g, "<br>")}</p>` : "").join("")}</article>`).join("")}
</section>`;
    };
    const el = document.createElement("div");
    el.innerHTML = `<style>h1{font-size:18px;text-align:center;margin:0}h2{font-size:12px;text-align:center;color:#555;font-weight:normal}h3{font-size:12px;margin:14px 0 4px;color:#1E3A5F}p{font-size:11px;line-height:1.5;margin:4px 0}.meta{font-family:monospace;font-size:10px;color:#777;text-align:center}u{text-decoration:none;background:#fff3c4;padding:0 2px}</style>
<p class="meta">${esc(t("tpl.lawyer_header"))} · ${new Date().toISOString().slice(0, 10)}</p>${block("professional")}${block("personal")}`;
    printDoc(el, { mode: "a4", title: "Contract templates — lawyer review" });
  };

  const saveSettings = async (patch) => {
    try {
      await API.patch("/shop/profile", patch);
      toast.success(t("msg.saved"));
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const toggleLang = (l) => {
    const next = data.langs.includes(l) ? data.langs.filter((x) => x !== l) : [...data.langs, l];
    if (!next.length) return;
    saveSettings({ contract_langs: LANGS.filter((x) => next.includes(x)) });
  };
  const setArt = (i, patch) => setTpl({ ...tpl, articles: tpl.articles.map((a, j) => (j === i ? { ...a, ...patch } : a)) });
  const setArtLang = (i, field, lang, v) => setArt(i, { [field]: { ...tpl.articles[i][field], [lang]: v } });
  const save = async () => {
    setBusy(true);
    try {
      await API.put(`/shop/contract-templates/${use}`, tpl);
      toast.success(t("msg.saved"));
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };
  const reset = async () => {
    if (!window.confirm(`${t("tpl.reset")}?`)) return;
    setBusy(true);
    try {
      await API.delete(`/shop/contract-templates/${use}`);
      toast.success(t("msg.saved"));
      load();
    } catch (err) { toast.error(fmtErr(err)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="contract-templates-card">
      <h3 className="font-heading text-lg font-medium text-slate-900">{t("tpl.title")}</h3>
      <p className="text-sm text-slate-500 mt-1 mb-4">{t("tpl.desc")}</p>
      <div className="grid lg:grid-cols-3 gap-4 mb-5">
        <div>
          <Label>{t("tpl.langs")}</Label>
          <div className="flex gap-3 mt-2">
            {LANGS.map((l) => (
              <label key={l} className="flex items-center gap-1.5 text-sm">
                <input type="checkbox" data-testid={`tpl-lang-${l}`} checked={data.langs.includes(l)} onChange={() => toggleLang(l)} />{LABEL[l]}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-1">{t("tpl.prevail")}</p>
        </div>
        <div>
          <Label>{t("tpl.allocation")}</Label>
          <Select value={data.payment_allocation} onValueChange={(v) => saveSettings({ payment_allocation: v })}>
            <SelectTrigger data-testid="tpl-allocation-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="contract">{t("tpl.alloc_contract")}</SelectItem>
              <SelectItem value="legacy">{t("tpl.alloc_legacy")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>{t("ct.item_use")}</Label>
          <Select value={use} onValueChange={setUse}>
            <SelectTrigger data-testid="tpl-use-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="professional" data-testid="tpl-use-professional">{t("ct.use_professional")}</SelectItem>
              <SelectItem value="personal" data-testid="tpl-use-personal">{t("ct.use_personal")}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-[11px] text-slate-500 mt-1" data-testid="tpl-version-label">
            {t("tpl.version")}: {tpl.version} · {tpl.version > 0 ? t("tpl.custom") : t("tpl.default")}
          </p>
        </div>
      </div>
      <div className="grid lg:grid-cols-3 gap-3 mb-3">
        {LANGS.map((l) => (
          <div key={l}>
            <Label className="text-xs">{t("tpl.doc_title")} ({LABEL[l]})</Label>
            <Input value={tpl.title?.[l] || ""} onChange={(e) => setTpl({ ...tpl, title: { ...tpl.title, [l]: e.target.value } })} className="mt-1 h-8 text-xs" />
            <Input value={tpl.subtitle?.[l] || ""} placeholder={t("tpl.doc_subtitle")} onChange={(e) => setTpl({ ...tpl, subtitle: { ...tpl.subtitle, [l]: e.target.value } })} className="mt-1 h-8 text-xs" />
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <Label className="text-xs">{t("tpl.items_after")}</Label>
        <Input type="number" min="1" value={tpl.items_after_no || 1} onChange={(e) => setTpl({ ...tpl, items_after_no: Number(e.target.value) })} className="h-8 w-20 text-xs" />
      </div>
      <p className="text-[11px] text-slate-500 font-mono mb-3 break-words">{t("tpl.placeholders")}</p>
      <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
        {tpl.articles.map((a, i) => (
          <details key={i} className="border border-slate-200 rounded-lg" data-testid={`tpl-article-${a.no}`}>
            <summary className="cursor-pointer px-3 py-2 text-sm font-medium flex items-center gap-2">
              <span className="font-mono text-slate-500">{t("tpl.article")} {a.no}</span>
              <span className="truncate flex-1">{a.title?.pt || a.title?.tet || a.title?.en}</span>
              <button type="button" className="p-1 hover:bg-red-50 rounded" onClick={(e) => { e.preventDefault(); setTpl({ ...tpl, articles: tpl.articles.filter((_, j) => j !== i) }); }}>
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
              </button>
            </summary>
            <div className="p-3 grid lg:grid-cols-3 gap-3 border-t border-slate-100">
              {LANGS.map((l) => (
                <div key={l}>
                  <Label className="text-xs">{LABEL[l]}</Label>
                  <Input value={a.title?.[l] || ""} onChange={(e) => setArtLang(i, "title", l, e.target.value)} className="mt-1 h-8 text-xs font-semibold" />
                  <textarea rows={8} value={a.body?.[l] || ""} onChange={(e) => setArtLang(i, "body", l, e.target.value)}
                    className="mt-1 w-full rounded-md border border-input bg-white px-2 py-1.5 text-xs" data-testid={`tpl-body-${a.no}-${l}`} />
                </div>
              ))}
            </div>
          </details>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <Button type="button" variant="outline" size="sm" data-testid="tpl-add-article"
          onClick={() => setTpl({ ...tpl, articles: [...tpl.articles, { no: (tpl.articles.at(-1)?.no || 0) + 1, title: { pt: "", tet: "", en: "" }, body: { pt: "", tet: "", en: "" } }] })}>
          <Plus className="h-3.5 w-3.5 mr-1" />{t("tpl.add_article")}
        </Button>
        <Button type="button" variant="outline" size="sm" data-testid="tpl-lawyer-print" onClick={printForLawyer}>
          <Printer className="h-3.5 w-3.5 mr-1" />{t("tpl.lawyer_print")}
        </Button>
        <div className="flex-1" />
        <Button type="button" variant="outline" size="sm" data-testid="tpl-reset-button" onClick={reset} disabled={busy}>{t("tpl.reset")}</Button>
        <Button type="button" size="sm" data-testid="tpl-save-button" onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white">{t("common.save")}</Button>
      </div>
    </div>
  );
}
